<template>
  <div class="loyalty-success-backdrop" @click.self="$emit('close')">
    <section
      class="loyalty-success-card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="loyalty-success-title"
      @keydown.esc="$emit('close')"
    >
      <button class="loyalty-success-close" type="button" aria-label="Cerrar confirmación" @click="$emit('close')">
        <i class="fa-solid fa-xmark" aria-hidden="true"></i>
      </button>

      <div class="loyalty-success-check" aria-hidden="true">
        <i class="fa-solid fa-check"></i>
      </div>
      <p class="loyalty-success-eyebrow">TU FIDELIDAD ESTÁ LISTA</p>
      <h2 id="loyalty-success-title" ref="title" tabindex="-1">
        {{ isNew ? '¡Registro completado!' : '¡Has iniciado sesión!' }}
      </h2>
      <p class="loyalty-success-intro">
        {{ isNew ? 'Ya puedes empezar a sumar puntos' : 'Ya puedes seguir sumando puntos' }}<template v-if="restaurantName"> en <strong>{{ restaurantName }}</strong></template>.
      </p>

      <div class="loyalty-success-balance" aria-label="Tu saldo de puntos">
        <span>Tu saldo actual</span>
        <strong>{{ points }} <small>pts</small></strong>
        <p>{{ points === 1 ? 'Tienes 1 punto disponible' : 'Tienes ' + points + ' puntos disponibles' }}</p>
      </div>
      <p class="loyalty-success-account">Cuenta: {{ email }}</p>

      <div v-if="showWallet" class="loyalty-success-wallet">
        <span class="loyalty-success-wallet-icon" aria-hidden="true"><i class="fa-solid fa-wallet"></i></span>
        <div>
          <strong>Lleva tu tarjeta en el {{ walletPlatform === 'google' ? 'móvil' : 'iPhone' }}</strong>
          <p>Añádela a {{ walletLabel }} para tener tus puntos y recompensas a mano. Te enviaremos un código por correo para confirmarlo.</p>
        </div>
        <button type="button" @click="$emit('add-wallet')">
          Añadir a {{ walletLabel }} <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
        </button>
      </div>

      <button v-if="reviewPreferences" class="loyalty-success-continue" type="button" @click="$emit('review-preferences')">Revisar privacidad y comunicaciones</button>
      <button class="loyalty-success-rewards" type="button" @click="$emit('view-rewards')">
        Ver mis recompensas
      </button>
      <button class="loyalty-success-continue" type="button" @click="$emit('close')">
        Seguir en la carta
      </button>
    </section>
  </div>
</template>

<script>
export default {
  name: 'LoyaltyLoginSuccess',
  props: {
    points: { type: Number, default: 0 },
    email: { type: String, default: '' },
    restaurantName: { type: String, default: '' },
    isNew: { type: Boolean, default: false },
    showWallet: { type: Boolean, default: false },
    walletPlatform: { type: String, default: 'apple' },
    reviewPreferences: { type: Boolean, default: false },
  },
  emits: ['close', 'view-rewards', 'add-wallet', 'review-preferences'],
  computed: {
    walletLabel() { return this.walletPlatform === 'google' ? 'Google Wallet' : 'Apple Wallet'; },
  },
  mounted() {
    this.$nextTick(() => this.$refs.title?.focus());
  },
};
</script>

<style scoped>
.loyalty-success-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10002;
  display: grid;
  place-items: center;
  padding: max(16px, env(safe-area-inset-top, 0px)) 16px max(16px, env(safe-area-inset-bottom, 0px));
  background: rgba(13, 27, 38, 0.68);
  backdrop-filter: blur(5px);
}

.loyalty-success-card {
  position: relative;
  width: min(100%, 420px);
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  padding: 28px 24px 22px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 20px 60px rgba(11, 29, 40, 0.28);
  text-align: center;
}

.loyalty-success-close {
  position: absolute;
  top: 12px;
  right: 12px;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: #f4f5f6;
  color: #425466;
}

.loyalty-success-check {
  display: grid;
  place-items: center;
  width: 66px;
  height: 66px;
  margin: 2px auto 14px;
  border-radius: 50%;
  background: #eaf8ee;
  color: #17844a;
  font-size: 1.9rem;
}

.loyalty-success-eyebrow {
  margin: 0 0 6px;
  color: var(--primary-color, #f47700);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.loyalty-success-card h2 {
  margin: 0 0 8px;
  color: #142333;
  font-size: clamp(1.35rem, 5vw, 1.7rem);
  font-weight: 800;
}

.loyalty-success-card h2:focus { outline: none; }

.loyalty-success-intro {
  margin: 0 auto 18px;
  max-width: 310px;
  color: #536276;
  font-size: 0.92rem;
  line-height: 1.45;
}

.loyalty-success-balance {
  padding: 16px;
  border: 1px solid #ffe4c7;
  border-radius: 18px;
  background: #fff8ef;
}

.loyalty-success-balance > span {
  display: block;
  color: #6e5a49;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.loyalty-success-balance strong {
  display: block;
  margin: 3px 0;
  color: #182638;
  font-size: clamp(2.8rem, 12vw, 4rem);
  font-weight: 800;
  line-height: 1.05;
}

.loyalty-success-balance small {
  font-size: 0.35em;
}

.loyalty-success-balance p {
  margin: 4px 0 0;
  color: #685a4a;
  font-size: 0.82rem;
}

.loyalty-success-account {
  margin: 12px 0 18px;
  color: #69788a;
  font-size: 0.78rem;
  overflow-wrap: anywhere;
}

.loyalty-success-wallet {
  display: grid;
  grid-template-columns: 38px 1fr;
  gap: 10px 12px;
  padding: 14px;
  border: 1px solid #d9e1e8;
  border-radius: 16px;
  background: #f7f9fb;
  text-align: left;
}

.loyalty-success-wallet-icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: #17202b;
  color: #fff;
  font-size: 1.05rem;
}

.loyalty-success-wallet strong {
  color: #172638;
  font-size: 0.88rem;
}

.loyalty-success-wallet p {
  margin: 4px 0 0;
  color: #526173;
  font-size: 0.78rem;
  line-height: 1.4;
}

.loyalty-success-wallet button {
  grid-column: 1 / -1;
  min-height: 44px;
  border: 0;
  border-radius: 10px;
  background: #161b22;
  color: #fff;
  font-weight: 700;
}

.loyalty-success-wallet button i { margin-left: 6px; }

.loyalty-success-rewards,
.loyalty-success-continue {
  width: 100%;
  min-height: 44px;
  border: 0;
  border-radius: 11px;
  font-weight: 700;
}

.loyalty-success-rewards {
  margin-top: 16px;
  background: var(--primary-color, #f47700);
  color: #fff;
}

.loyalty-success-continue {
  margin-top: 5px;
  background: transparent;
  color: #526173;
}

@media (max-height: 650px) {
  .loyalty-success-card { padding-top: 20px; }
  .loyalty-success-check { width: 50px; height: 50px; margin-bottom: 9px; font-size: 1.4rem; }
  .loyalty-success-intro { margin-bottom: 12px; }
  .loyalty-success-account { margin-bottom: 12px; }
}

@media (prefers-reduced-motion: reduce) {
  .loyalty-success-card { scroll-behavior: auto; }
}
</style>
